import React from 'react'
import { Link } from 'react-router-dom'
import {
    Button,
    Drawer,
    Grid,
    Icon,
    Pagination,
} from '@softwaredev_cegeka/stix-core'

import { AuthApi } from 'src/api/authApi'
import { authenticatedUserStore, notifierStore } from 'src/stores'
import { TenantTable } from 'src/components/EntityTable'
import TenantDrawerContent from 'src/components/Drawer/TenantDrawerContent'
import DrawerHeader from 'src/components/Drawer/DrawerHeader'

class TenantRoute extends React.Component {
    state = {
        tenants: [],
        companies: [],
        selectedTenant: null,
        page: 1,
        totalPages: 1,
        totalElements: 1,
        loading: false,
        params: {},
        userIsTenantAdmin: false,
    }

    authClient = new AuthApi(authenticatedUserStore.authToken)

    componentDidMount() {
        notifierStore.setShowMessage(false)
        this.fetchAndStoreTenants()
        this.fetchAndStoreCompanies()
        this.checkIfTenantAdmin(this.props.organizationId).then(res => {
            this.setState({
                userIsTenantAdmin: res,
                loading: false,
            })
        })
    }

    // Cancel still ongoing get requests, to prevent updating state and changing the page to the previous clicked link
    componentWillUnmount() {
        this.authClient.cancelRequest()
    }

    checkIfTenantAdmin = async currentOrganizationId => {
        let isTenantAdmin = false
        try {
            const response = await this.authClient.fetchUsers(
                1,
                null,
                {
                    per_page: 1000,
                    user_is_admin: true,
                    username_like: authenticatedUserStore.userData.username,
                },
                currentOrganizationId
            )
            const tenantAdmins = response.data.results
            isTenantAdmin = tenantAdmins.length > 0
            return isTenantAdmin
        } catch (e) {
            return isTenantAdmin
        }
    }

    checkIfCegekaUser = () => {
        if (
            this.props.selectedOrganization &&
            this.props.selectedOrganization?.value === '656'
        ) {
            return true
        } else {
            if (authenticatedUserStore.userData.organization_id === '656') {
                return true
            }
        }
        return false
    }

    rowSelectHandler = async (selectObj, setDrawerState) => {
        const { selectedRow } = selectObj

        this.setState(
            {
                selectedTenant: selectedRow.original.id,
            },
            () => setDrawerState(true)
        )
    }

    fetchAndStoreTenants = (page = this.state.page) => {
        this.setState({ loading: true })
        this.authClient
            .fetchTenants(page, this.props.history, this.state.params)
            .then(response => {
                this.setState({
                    tenants: response?.data?.results,
                    totalPages: response?.data?._page.total_pages,
                    totalElements: response?.data?._page.total_elements,
                    loading: false,
                    page: page,
                })
            })
            .catch(err => console.error(err))
    }

    fetchAndStoreCompanies = () => {
        this.authClient
            .fetchCompanies()
            .then(response => {
                this.setState({
                    companies: response?.data?.results,
                })
            })
            .catch(err => console.error(err))
    }

    setParams = params => {
        this.setState(
            {
                params: params,
            },
            () => this.fetchAndStoreTenants(1)
        )
    }

    handlePageChange = page => {
        this.setState({ page: page }, () => this.fetchAndStoreTenants())
    }

    getSelectedTenant() {
        return this.state.tenants.find(tenant => {
            return tenant.id === this.state.selectedTenant
        })
    }

    unlinkUserAsTenantAdmin = userId => {
        this.authClient
            .removeUserAsTenantAdmin(userId, this.state.selectedTenant)
            .then(response => {
                if (response.status === 200 || response.status === 204) {
                    this.fetchAndStoreTenants()
                    notifierStore.setToastInfo(
                        'Tenant admin successfully unlinked from tenant',
                        true
                    )
                } else {
                    notifierStore.setToastInfo(
                        'Failed to unlink tenant admin from the tenant',
                        false
                    )
                }
            })
            .catch(err => {
                notifierStore.setToastInfo(err.response?.data?.error, false)
            })
            .finally(notifierStore.setShowMessage(true))
    }

    addUserAsTenantAdmin = userId => {
        this.authClient
            .setUserAsTenantAdmin(userId, this.state.selectedTenant)
            .then(response => {
                if (response.status === 200 || response.status === 204) {
                    this.fetchAndStoreTenants()
                    notifierStore.setToastInfo(
                        'User successfully set as tenant admin',
                        true
                    )
                } else {
                    notifierStore.setToastInfo(
                        'Failed to set the user as tenant admin',
                        false
                    )
                }
            })
            .catch(err => {
                notifierStore.setToastInfo(err.response?.data?.error, false)
            })
            .finally(notifierStore.setShowMessage(true))
    }

    removeTenant = (tenantId, tenantName) => {
        this.authClient
            .deleteTenant(tenantId)
            .then(response => {
                if (response.status === 204) {
                    this.fetchAndStoreTenants()
                    notifierStore.setToastInfo(
                        `Tenant ${tenantName} successfully deleted`,
                        true
                    )
                } else {
                    notifierStore.setToastInfo(
                        `Failed to delete tenant ${tenantName}`,
                        false
                    )
                }
            })
            .catch(err => {
                notifierStore.setToastInfo(err.response?.data?.error, false)
            })
            .finally(notifierStore.setShowMessage(true))
    }

    render() {
        const selectedTenant = this.getSelectedTenant() || {}
        const isCegekaUser = this.checkIfCegekaUser()
        const displayCreateButton =
            (isCegekaUser && this.state.userIsTenantAdmin) ||
            authenticatedUserStore.isGlobalAdmin
        return (
            <div className="entity-drawer">
                <Drawer
                    type="fixed"
                    panelTitle={
                        <DrawerHeader
                            path={`/tenant-form/${selectedTenant.id}`}
                            title={selectedTenant.name}
                        />
                    }
                    panelContent={
                        <TenantDrawerContent
                            {...this.props}
                            selectedTenant={selectedTenant}
                            unlinkUserAsTenantAdmin={
                                this.unlinkUserAsTenantAdmin
                            }
                            addUserAsTenantAdmin={this.addUserAsTenantAdmin}
                            updateTenantList={this.fetchAndStoreTenants}
                        />
                    }
                >
                    <Grid.Container>
                        <div className={'page-header'}>
                            <h1 className={'content-title'}>
                                Tenants overview
                            </h1>
                            {process.env
                                .REACT_APP_ENABLE_EDIT_TENANT_FUNCTIONALITY ===
                                'true' &&
                                displayCreateButton && (
                                    <Link
                                        to={{
                                            pathname: '/tenant-form',
                                        }}
                                    >
                                        <Button variant="secondary">
                                            <Icon icon="add" />
                                            <span>Create tenant</span>
                                        </Button>
                                    </Link>
                                )}
                        </div>

                        <Drawer.Context.Consumer>
                            {({ setDrawerState }) => (
                                <div className="data-table-container">
                                    <TenantTable
                                        data={this.state.tenants}
                                        companies={this.state.companies}
                                        loading={this.state.loading}
                                        params={this.state.params}
                                        setParams={this.setParams}
                                        rowSelectHandler={selectObj =>
                                            this.rowSelectHandler(
                                                selectObj,
                                                setDrawerState
                                            )
                                        }
                                        deleteAction={this.removeTenant}
                                    />
                                    {this.state.totalPages >= 2 && (
                                        <div className="pagination-wrapper">
                                            <Pagination
                                                lastPage={this.state.totalPages}
                                                currentPage={this.state.page}
                                                onClick={
                                                    this.state.loading
                                                        ? () => {}
                                                        : this.handlePageChange
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </Drawer.Context.Consumer>
                    </Grid.Container>
                </Drawer>
            </div>
        )
    }
}

export default TenantRoute
